import { SkyButton, SkyDrawer, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import {
  OFFBOARDING_ITEM_LABELS,
  type OffboardingItem,
  type OffboardingList,
} from "@shared/types/tenantOffboarding";
import type { TenantView } from "@shared/types/tenants";
import { useTenantOffboarding } from "@/hooks/use-tenants";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { ErrorState, GridSkeleton } from "./ScreenState";

/**
 * A deleted tenant's offboarding list, read-only (F32 PR3): every resource it left behind, in the
 * order Skypoint's Infra team removes them. skyData removed nothing; Infra decides when. The list
 * is copied as plain text, one line per item, so it can go straight into a ticket.
 */

/** One line of the copied text: the label, then the id or name, then its note. */
function itemLine(item: OffboardingItem): string {
  const parts = [`${OFFBOARDING_ITEM_LABELS[item.kind]}: ${item.id ?? item.name}`];
  if (item.id && item.name !== item.id) parts.push(`(${item.name})`);
  if (item.scope) parts.push(`scope ${item.scope}`);
  if (item.principal) parts.push(`principal ${item.principal}`);
  if (item.vault) parts.push(`in vault ${item.vault}`);
  if (item.note) parts.push(`. ${item.note}`);
  return parts.join(" ").replace(" . ", ". ");
}

/** The whole list as text, for the clipboard. */
export function offboardingText(list: OffboardingList, zone: string): string {
  return [
    `Offboarding list for tenant ${list.displayName} (${list.tenantName}, ${list.tenantId})`,
    `Deleted ${formatInstant(list.deletedAt, zone, "seconds", { named: true })}. Resources: ${list.resourcesMode}.`,
    "",
    ...list.items.map(itemLine),
  ].join("\n");
}

function LoadedList({ list }: { list: OffboardingList }) {
  const zone = useDisplayTz();
  const copyError = useInlineError();
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    copyError.clear();
    setCopied(false);
    try {
      await navigator.clipboard.writeText(offboardingText(list, zone));
      setCopied(true);
    } catch (error) {
      copyError.show(error, "The list could not be copied. Select it and copy it instead.");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <SkyMessageBar
        intent="info"
        layout="multiline"
        title="skyData removed nothing"
        message="Skypoint's Infra team removes each item below when its audit retention allows, in this order. The Infra team also received this list."
        testId="tenant-offboarding-note"
      />
      <SkyText size={200} color="muted" block>
        Deleted{" "}
        <span title={instantHoverTitle(list.deletedAt, zone)}>
          {formatInstant(list.deletedAt, zone)}
        </span>
        . Items marked "may hold PHI" stay under Infra's retention before they are removed.
      </SkyText>
      <div className="flex items-center gap-3">
        <SkyButton
          appearance="primary"
          onClick={() => void copy()}
          testId="tenant-offboarding-copy"
        >
          Copy the list
        </SkyButton>
        {copied ? (
          <SkyText size={200} color="muted" testId="tenant-offboarding-copied">
            Copied.
          </SkyText>
        ) : null}
      </div>
      <InlineErrorBar error={copyError.error} onDismiss={copyError.clear} />
      <ol className="flex flex-col gap-2" data-testid="tenant-offboarding-items">
        {list.items.map((item, index) => (
          <li key={`${item.kind}-${item.id ?? item.name}-${String(index)}`} className="min-w-0">
            <SkyText size={200} weight="medium" block>
              {OFFBOARDING_ITEM_LABELS[item.kind]}
              {item.mayHoldPhi ? " (may hold PHI)" : ""}
            </SkyText>
            <SkyText size={200} font="monospace" block wrap className="break-all">
              {item.id ?? item.name}
            </SkyText>
            {item.scope || item.principal || item.vault || item.note ? (
              <SkyText size={200} color="muted" block wrap className="break-all">
                {[
                  item.scope ? `Scope ${item.scope}` : null,
                  item.principal ? `Principal ${item.principal}` : null,
                  item.vault ? `In vault ${item.vault}` : null,
                  item.note,
                ]
                  .filter(Boolean)
                  .join(". ")}
              </SkyText>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function TenantOffboardingDrawer({
  tenant,
  onClose,
}: {
  /** The deleted tenant whose list is open; null closes the drawer. */
  tenant: Pick<TenantView, "id" | "displayName"> | null;
  onClose: () => void;
}) {
  const list = useTenantOffboarding(tenant?.id ?? null);
  if (!tenant) return null;

  let body;
  if (list.isPending) body = <GridSkeleton rows={6} label="Loading the offboarding list…" />;
  else if (list.isError) {
    body = (
      <ErrorState
        title="The offboarding list did not load"
        error={list.error}
        onRetry={() => void list.refetch()}
      />
    );
  } else body = <LoadedList list={list.data} />;

  return (
    <SkyDrawer
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      position="end"
      size="medium"
      headerTitle={`Offboarding list for ${tenant.displayName}`}
      testId="tenant-offboarding-drawer"
    >
      {body}
    </SkyDrawer>
  );
}
